"use client";

import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker, useDayPicker, useNavigation } from "react-day-picker";
import { format, setMonth, setYear, startOfMonth } from "date-fns";
import { es } from "date-fns/locale";

import { cn } from "@hipotecar/ui/lib/utils";
import { buttonVariants } from "@hipotecar/ui/button";

import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

interface CalendarCaptionProps {
	displayMonth: Date;
}

function CalendarCaption({ displayMonth }: CalendarCaptionProps) {
	const { fromDate, toDate, locale } = useDayPicker();
	const { goToMonth, nextMonth, previousMonth } = useNavigation();

	// use displayMonth directly
	const date = displayMonth;

	const currentMonthNumber = date.getMonth();
	const currentYear = date.getFullYear();

	// Generate localized months
	const months = React.useMemo(() => {
		const _months: { value: number; label: string }[] = [];
		for (let i = 0; i < 12; i++) {
			const mDate = setMonth(startOfMonth(new Date()), i);
			const label = format(mDate, "MMM", { locale: locale || es });
			_months.push({
				value: i,
				label: label.charAt(0).toUpperCase() + label.slice(1), // Capitalize
			});
		}
		return _months;
	}, [locale]);

	// Generate years from fromDate or 2010 to toDate or current year + 30
	const years = React.useMemo(() => {
		const startYear = fromDate ? fromDate.getFullYear() : 2010;
		const endYear = toDate
			? toDate.getFullYear()
			: new Date().getFullYear() + 30;
		const _years: number[] = [];
		for (let y = startYear; y <= endYear; y++) {
			_years.push(y);
		}
		return _years;
	}, [fromDate, toDate]);

	const handleMonthChange = (value: string) => {
		const newMonth = parseInt(value);
		const newDate = setMonth(date, newMonth);
		goToMonth(newDate);
	};

	const handleYearChange = (value: string) => {
		const newYear = parseInt(value);
		const newDate = setYear(date, newYear);
		goToMonth(newDate);
	};

	const isMonthDisabled = (month: number) => {
		if (!fromDate && !toDate) return false;

		const firstOfMonth = new Date(currentYear, month, 1);
		const lastOfMonth = new Date(currentYear, month + 1, 0);

		if (fromDate && lastOfMonth < startOfMonth(fromDate)) return true;
		if (toDate && firstOfMonth > startOfMonth(toDate)) return true;

		return false;
	};

	return (
		<div className="flex items-center justify-between gap-2 sm:justify-center">
			<button
				className={cn(
					buttonVariants({ variant: "outline" }),
					"size-7 border-none bg-transparent p-0 opacity-50 shadow-none hover:bg-transparent hover:opacity-100",
				)}
				onClick={() => previousMonth && goToMonth(previousMonth)}
				disabled={!previousMonth}
			>
				<ChevronLeft className="size-4" />
			</button>

			<div className="flex gap-2">
				<Select
					value={currentMonthNumber.toString()}
					onValueChange={handleMonthChange}
				>
					<SelectTrigger className="h-7 w-[90px] border-none bg-background font-medium shadow-none focus:ring-0 focus:ring-offset-0">
						<SelectValue>{months[currentMonthNumber]?.label}</SelectValue>
					</SelectTrigger>
					<SelectContent>
						{months.map((m) => (
							<SelectItem
								key={m.value}
								value={m.value.toString()}
								disabled={isMonthDisabled(m.value)}
							>
								{m.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>

				<Select value={currentYear.toString()} onValueChange={handleYearChange}>
					<SelectTrigger className="h-7 w-[90px] border-none bg-background font-medium shadow-none focus:ring-0 focus:ring-offset-0">
						<SelectValue>{currentYear}</SelectValue>
					</SelectTrigger>
					<SelectContent>
						{years.map((year) => (
							<SelectItem key={year} value={year.toString()}>
								{year}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			<button
				className={cn(
					buttonVariants({ variant: "outline" }),
					"size-7 border-none bg-transparent p-0 opacity-50 shadow-none hover:bg-transparent hover:opacity-100",
				)}
				onClick={() => nextMonth && goToMonth(nextMonth)}
				disabled={!nextMonth}
			>
				<ChevronRight className="size-4" />
			</button>
		</div>
	);
}

function Calendar({
	className,
	classNames,
	showOutsideDays = true,
	...props
}: CalendarProps) {
	const handleMonthChange = (newMonth: Date) => {
		if (props.mode === "single" && props.selected && props.onSelect) {
			const selectedDate = props.selected as Date;
			const newSelectedDate = setMonth(selectedDate, newMonth.getMonth());
			const finalSelectedDate = setYear(
				newSelectedDate,
				newMonth.getFullYear(),
			);
			(
				props.onSelect as (
					date: Date,
					selected: Date,
					modifiers: Record<string, boolean>,
					e: React.MouseEvent | React.KeyboardEvent,
				) => void
			)(
				finalSelectedDate,
				finalSelectedDate,
				{ viewChange: true },
				{} as React.MouseEvent,
			);
		}
		props.onMonthChange?.(newMonth);
	};

	return (
		<DayPicker
			showOutsideDays={showOutsideDays}
			className={cn("p-3", className)}
			onMonthChange={handleMonthChange}
			classNames={{
				months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
				month: "space-y-4",
				caption: "flex justify-center pt-1 relative items-center",
				caption_label: "hidden", // We use custom caption
				nav: "space-x-1 flex items-center",
				nav_button: cn(
					buttonVariants({ variant: "outline" }),
					"size-7 bg-transparent p-0 opacity-50 hover:opacity-100",
				),
				nav_button_previous: "absolute left-1",
				nav_button_next: "absolute right-1",
				table: "w-full border-collapse space-y-1",
				head_row: "flex",
				head_cell:
					"text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
				row: "flex w-full mt-2",
				cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
				day: cn(
					buttonVariants({ variant: "ghost" }),
					"size-9 p-0 font-normal aria-selected:opacity-100",
				),
				day_range_end: "day-range-end",
				day_selected:
					"bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
				day_today: "bg-accent text-accent-foreground",
				day_outside:
					"day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
				day_disabled: "text-muted-foreground opacity-50",
				day_range_middle:
					"aria-selected:bg-accent aria-selected:text-accent-foreground",
				day_hidden: "invisible",
				...classNames,
			}}
			components={{
				IconLeft: () => <ChevronLeft className="size-4" />,
				IconRight: () => <ChevronRight className="size-4" />,
				CaptionLabel: CalendarCaption,
			}}
			{...props}
		/>
	);
}
Calendar.displayName = "Calendar";

export { Calendar };
